import { FC, KeyboardEventHandler, ReactNode, Ref } from "react";
import classNames from "classnames";
import { PlainModal } from "@design-system/Modal/Modal";
import { Command } from "@vendor/cmdk/cmdk";
import styles from "./CommandPalette.module.scss";

export interface CommandPaletteProps {
    show: boolean;
    onHide: () => void;
    search: string;
    onSearchChange: (value: string) => void;
    inputPlaceholder?: string;
    onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
    filter?: (value: string, search: string, keywords?: string[]) => number;
    breadcrumbs?: ReactNode;
    footer?: ReactNode;
    /** Shown when nothing matches; `null` for a step whose content isn't items (a calendar). */
    emptyLabel?: ReactNode;
    className?: string;
    dialogClassName?: string;
    headerClassName?: string;
    footerClassName?: string;
    commandRef?: Ref<HTMLDivElement>;
    inputRef?: Ref<HTMLInputElement>;
    children?: ReactNode;
}

/**
 * Presentational ⌘K palette chrome: modal shell, cmdk root, sticky header with
 * a breadcrumb slot and search input, results region, and an optional footer.
 * All state (open, search, directory stack, filtering) belongs to the caller.
 */
export const CommandPalette: FC<CommandPaletteProps> = ({
    show,
    onHide,
    search,
    onSearchChange,
    inputPlaceholder = "Search or type a command",
    onKeyDown,
    filter,
    breadcrumbs,
    footer,
    emptyLabel = "No results found.",
    className,
    dialogClassName,
    headerClassName,
    footerClassName,
    commandRef,
    inputRef,
    children,
}) => (
    <PlainModal
        show={show}
        onHide={onHide}
        className={classNames(styles.commandPalette, className)}
        dialogClassName={classNames(styles.dialog, dialogClassName)}
        backdrop={false}
    >
        <Command
            label="Commands"
            loop
            onKeyDown={onKeyDown}
            filter={filter}
            ref={commandRef}
        >
            <Command.List>
                <header
                    className={classNames(
                        styles.header,
                        styles.overlay,
                        headerClassName,
                    )}
                >
                    {breadcrumbs}

                    <form>
                        <div className={styles.searchField}>
                            <Command.Input
                                value={search}
                                onValueChange={onSearchChange}
                                className={styles.searchInput}
                                placeholder={inputPlaceholder}
                                autoFocus
                                ref={inputRef}
                            />
                        </div>
                    </form>
                </header>

                <main>
                    {emptyLabel !== null && (
                        <Command.Empty>{emptyLabel}</Command.Empty>
                    )}
                    {children}
                </main>

                {footer ? (
                    <footer
                        className={classNames(
                            styles.footer,
                            styles.overlay,
                            footerClassName,
                        )}
                    >
                        {footer}
                    </footer>
                ) : null}
            </Command.List>
        </Command>
    </PlainModal>
);
